import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/joy';
import Card from 'components/core/Card';
import Stack from 'components/core/Stack';
import Typography from 'components/core/Typography';
import Grid from 'components/core/Grid';
import Sheet from 'components/core/Sheet';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import StarIcon from '@mui/icons-material/Star';

import InfoLine from './InfoLine';
import DossierActionButtons from './DossierActionButtons';
import DossierPrintButton from './DossierPrintButton';

import Observation from 'domain/Observation';




const ObservationDossier = ({observation, notes, taxonomyNameSynonyms, alternateIdentifiers}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const dossierRef = useRef();

    if (
        !observation || 
        !observation.get || 
        !observation.get('name') ||
        !observation.get('name')?.get('taxonomy') ||
        !observation.get('name')?.get('taxonomy').getByPath
    ) return null;
    

    const latitude = observation.getByPath(['lat'])
    const longitude = observation.getByPath(['lng'])
    const taxonomy = observation.get('name').get('taxonomy');


    const dossierSchema = [
        {
            id: 'date',
            label: 'Date',
            value: observation.get('event')?.get('startDate') || observation.get('date'),
            dateSpecificity: observation.get('dateSpecificity')
        },
        {
            id: 'observedBy',
            label: 'Observed By',
            value: observation.get('observedBy')
        },
        {
            id: 'location',
            label: 'Location',
            value: observation.get('location')
        },
        {
            id: 'town',
            label: 'Town',
            value: observation.get('town')
        },
        {
            id: 'quality',
            label: 'Quality',
            value: observation.get('quality')
        },
        {
            id: 'substantiation',
            label: 'Substantiation',
            value: observation.get('substantiation')
        },
        {
            id: 'keywords',
            label: 'Keywords',
            value: observation.get('keyword')
        },
        {
            id: 'event',
            label: 'Event',
            value: observation.get('event')
        },
        {
            id: 'latLong',
            label: 'Latitude/Longitude',
            value: latitude + ', ' + longitude
        },
        {
            id: 'substrate',
            label: 'Substrate',
            value: observation.get('substrate')
        },
        {
            id: 'locDeterminization',
            label: 'Location Determined Method',
            value: observation.get('locDeterminization')
        },
        {
            id: 'isDetected',
            label: 'Is Detected',
            value: observation.get('isDetected') ? 'Yes' : 'No'
        },
        {
            id: 'associatedObservations',
            label: 'Associated Observations',
            value: ''
        },
        {
            id: 'observationRestriction',
            label: 'Observation Restriction',
            value: observation.get('observationRestriction') ? 'Yes' : 'No'
        },
    ];

    const synonyms = taxonomyNameSynonyms.map(synonym => synonym.get('name'))
        .filter(synonym => synonym !== taxonomy?.get('primaryName'))
        .join(', ');

    const getParents = (taxon, index) => {
        if (!taxon) return [];
        const taxonData = {
            name: taxon?.get('primaryName'),
            id: taxon.id
        }
        if (!taxon.get('parent')) return [taxonData];
        return [...getParents(taxon.get('parent'), index + 1), taxonData];
    }


    return (
        <Sheet 
            ref={dossierRef}
            sx={{
                width: '100%',
                background: 'none',
                mt: 2,
                mb: 2
            }}
        >
            <Card>
                <Grid container >
                    <Grid item xs={12}>
                        <Stack direction='row' spacing={6} alignItems='end' justifyContent='space-between'>
                            <Stack direction='row' spacing={2} alignItems='end'>
                                <Typography variant='h3'>
                                    OBSERVATION {taxonomy.primaryName} {observation.get('label') ? `(${observation.get('label')})` : ''}
                                </Typography>
                            </Stack>
                            <Stack direction='row' spacing={1} className="dossier-action-buttons">
                                <DossierPrintButton 
                                    dossierRef={dossierRef} 
                                    title={`Observation - ${taxonomy.primaryName} ${observation.get('label') ? `(${observation.get('label')})` : ''}`} 
                                />
                                <DossierActionButtons className="Observation" id={observation.id} />
                            </Stack>
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack sx={{mb: 2}} direction='row' spacing={2}>
                            <Breadcrumbs>
                                {getParents(taxonomy.get('parent'), 0).map((parent, index) => (
                                    <Typography key={index}>
                                        {parent.name}
                                    </Typography>
                                ))}
                            </Breadcrumbs>
                        </Stack>
                    </Grid>
                    <InfoLine 
                        id='subject'
                        label='Subject'
                        value={taxonomy.get('subject')}
                    />
                    <InfoLine
                        id='synonym'
                        label='Synonym'
                        value={synonyms}
                    />
                    {dossierSchema.map(({label, value, id, dateSpecificity}) => (
                        <InfoLine
                            key={id}
                            id={id}
                            label={label}
                            value={value}
                            dateSpecificity={dateSpecificity}
                        />
                    ))}
                    <Grid item xs={12} id={'alternateIdentifiers'}>
                        <Stack sx={{mb: 2}}>
                            <Typography variant='body2'>
                                Alternate Identifiers
                            </Typography>
                            <Stack direction='row' spacing={3} alignItems='end' style={{marginBottom: 6}} flexWrap="wrap">
                                {alternateIdentifiers
                                    .sort((a, b) => (b.get('primary') || b.primary) - (a.get('primary') || a.primary))
                                    .map((alternateIdentifier, index) => (
                                    <Stack direction='row' key={index} alignItems='center' spacing={0.25}>
                                        {(alternateIdentifier.get('primary') || alternateIdentifier.primary) && (
                                            <StarIcon sx={{ fontSize: '16px', color: 'primary.main' }} />
                                        )}
                                        <Typography 
                                            variant={'body2'}
                                        >
                                            {alternateIdentifier.get('system')?.get('label')}
                                        </Typography>
                                        <Typography 
                                            variant={'body1'}
                                        >
                                            {alternateIdentifier.get ? alternateIdentifier.get('identifier') : alternateIdentifier.identifier}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    </Grid>
                    <InfoLine
                        id='reviewedBy'
                        label='Reviewed By'
                        value={observation.get('reviewedBy')}
                    />
                    <Grid item xs={12}>
                        {notes && notes.length > 0 && (
                            <Stack>
                                <Typography variant='body2'>
                                    Notes
                                </Typography>
                                <Card sx={{background: theme.palette.primary[50]}}>
                                    <Grid container>
                                        {notes.map((note) => (
                                            <>
                                                <Grid item xs={3}>
                                                    <Typography variant='body2'>
                                                        {note.getByPath(['type', 'label'])}:
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={9}>
                                                    <Typography variant='body1'>
                                                        {note.getByPath(['text'])}
                                                    </Typography>
                                                </Grid>
                                            </>
                                        ))}
                                    </Grid>
                                </Card>
                            </Stack>
                        )}
                    </Grid>
                </Grid>
            </Card>
        </Sheet>
    )
} 

ObservationDossier.propTypes = {
    observation: PropTypes.instanceOf(Observation).isRequired,
    notes: PropTypes.array,
    taxonomyNameSynonyms: PropTypes.array,
    alternateIdentifiers: PropTypes.array
};

ObservationDossier.defaultProps = {
    notes: null,
    taxonomyNameSynonyms: [],
    alternateIdentifiers: []
};

export default ObservationDossier;